/**
 * Get set of reuseable routes for dati-abitazione (Dati abitazione) with a prefix. i.e: `preventivo-casa`, `modify-quotation`
 */
const homeDataRoutes = <T extends string>(prefix: T) => {
  const HOME_DATA = 'dati-abitazione';
  return {
    route: `${prefix}/${HOME_DATA}`,
    ZIP_CODE: {
      route: `${prefix}/${HOME_DATA}/cap`,
    },
    HOME_TYPE: {
      route: `${prefix}/${HOME_DATA}/tipo-abitazione`,
    },
    FLOOR_NUMBER: {
      route: `${prefix}/${HOME_DATA}/piano`,
    },
    HOME_SIZE: {
      route: `${prefix}/${HOME_DATA}/mq`,
    },
    ALARM_TYPE: {
      route: `${prefix}/${HOME_DATA}/antifurto`,
    },
    HOME_REGIME: {
      route: `${prefix}/${HOME_DATA}/tipo-contraente`,
    },
    HOME_USE: {
      route: `${prefix}/${HOME_DATA}/utilizzo-abitazione`,
    },
    MORTGAGE: {
      route: `${prefix}/${HOME_DATA}/mutuo`,
    },
    MODIFY_HOME_ADDRESS: {
      route: `${prefix}/${HOME_DATA}/indirizzo-abitazione`,
    },
  } as const;
};

/**
 * Get set of reuseable routes for dati-polizza (Dati poliza) with a prefix. i.e: `preventivo-casa`, `modify-quotation`
 */
const policyDataRoutes = <T extends string>(prefix: T) => {
  const POLICY_DATA = 'dati-polizza';
  return {
    route: `${prefix}/${POLICY_DATA}`,
    VERTI_POLICY: {
      route: `${prefix}/${POLICY_DATA}/gia-cliente`,
    },
    STARTING_DATE: {
      route: `${prefix}/${POLICY_DATA}/decorrenza`,
    },
    PACKAGES: {
      route: `${prefix}/${POLICY_DATA}/pacchetti`,
    },
    COVERAGES: {
      route: `${prefix}/${POLICY_DATA}/garanzie`,
    },
    EMAIL: {
      route: `${prefix}/${POLICY_DATA}/email-contatto`,
    },
  } as const;
};

/**
 * Get set of reuseable routes for dati-contratto (Dati contraente) with a prefix. i.e: `preventivo-casa`, `modify-quotation`
 */
const policyHolderDataRoutes = <T extends string>(prefix: T) => {
  const POLICY_HOLDER_DATA = 'dati-contratto';
  return {
    route: `${prefix}/${POLICY_HOLDER_DATA}`,
    POLICY_PARTICIPANT: {
      route: `${prefix}/${POLICY_HOLDER_DATA}/scelta-contraente`,
    },
    POLICY_HOLDER_DETAIL: {
      route: `${prefix}/${POLICY_HOLDER_DATA}/dati-anagrafici`,
    },
    POLICY_HOLDER_RESIDENCE: {
      route: `${prefix}/${POLICY_HOLDER_DATA}/residenza-contraente`,
    },
    HOME_ADDRESS: {
      route: `${prefix}/${POLICY_HOLDER_DATA}/indirizzo-abitazione`,
    },
  } as const;
};

/**
 * Get set of reuseable routes for dati-aggiuntivi (Informazioni aggiuntive) with a prefix. i.e: `preventivo-casa`, `modify-quotation`
 */
const additionalDataRoutes = <T extends string>(prefix: T) => {
  const ADDITIONAL_DATA = 'dati-aggiuntivi';
  return {
    route: `${prefix}/${ADDITIONAL_DATA}`,
    PARTNERSHIP: {
      route: `${prefix}/${ADDITIONAL_DATA}/partnership`,
    },
    POLICY_FORM: {
      route: `${prefix}/${ADDITIONAL_DATA}/altra-polizza`,
    },
    MORTGAGE_FORM: {
      route: `${prefix}/${ADDITIONAL_DATA}/dati-mutuo`,
    },
  } as const;
};

/**
 * Get set of reuseable routes for conferma-dati (Conferma e paga) with a prefix. i.e: `preventivo-casa`, `modify-quotation`
 */
const confirmDataRoutes = <T extends string>(prefix: T) => {
  const CONFIRM_DATA = 'conferma-dati';
  return {
    route: `${prefix}/${CONFIRM_DATA}`,
    SUMMARY: {
      route: `${prefix}/${CONFIRM_DATA}/riepilogo`,
    },
    SAVE_QUOTE: {
      route: `${prefix}/${CONFIRM_DATA}/salvataggio`,
    },
  } as const;
};

const cancelRoutes = <T extends string>(prefix: T) => {
  const CANCEL = 'cancellazione';
  return {
    route: `${prefix}/${CANCEL}`,
    REASONS: {
      route: `${prefix}/${CANCEL}/motivo-cancellazione`,
    },
    CONDITIONS: {
      route: `${prefix}/${CANCEL}/condizioni-cancellazione`,
    },
    SUMMARY: {
      route: `${prefix}/${CANCEL}/riepilogo-cancellazione`,
    },
  } as const;
};

const discontinueRoutes = <T extends string>(prefix: T) => {
  return {
    DISCONTINUE_POLICY_SUMMARY: {
      route: `${prefix}/disdetta/riepilogo-disdetta`,
    },
    DISCONTINUE_POLICY_REASON: {
      route: `${prefix}/disdetta/motivo-disdetta`,
    },
    DISCONTINUE_POLICY_REASON_DETAIL: {
      route: `${prefix}/disdetta/dettaglio-disdetta`,
    },
  } as const;
};

const renewalRoutes = <T extends string>(prefix: T) => {
  return {
    COVERAGES_RENEWAL: {
      route: `${prefix}/dati-polizza/garanzie`,
    },
    SUMMARY_RENEWAL: {
      route: `${prefix}/conferma-dati/riepilogo`,
    },
    SAVE_QUOTE_RENEWAL: {
      route: `${prefix}/conferma-dati/salvataggio`,
    },
    POLICY_RENEWAL_SUMMARY: {
      route: `${prefix}/riepilogo`,
    },
    ALARM_TYPE_RENEWAL: {
      route: `${prefix}/dati-abitazione/antifurto`,
    },
    VETI_POLICY_RENEWAL: {
      route: `${prefix}/dati-polizza/gia-cliente`,
    },
    POLICY_HOLDER_RESIDENCE_RENEWAL: {
      route: `${prefix}/dati-contratto/residenza-contraente`,
    },
    POLICY_FORM_RENEWAL: {
      route: `${prefix}/dati-aggiuntivi/altra-polizza`,
    },
  } as const;
};

export enum Flow {
  NEW_QUOTATION = 'NEW_QUOTATION',
  MODIFY_QUOTATION = 'MODIFY_QUOTATION',
  MODIFY_POLICY = 'MODIFY_POLICY',
  MODIFY_RENEWAL = 'MODIFY_RENEWAL',
}

/**
 * Project routes. Through each router module we control which are the routes available for each flow
 */
const projectRoutes = <T extends string>(prefix: T) =>
  ({
    route: prefix,
    HOME_DATA: homeDataRoutes(prefix),
    POLICY_DATA: policyDataRoutes(prefix),
    POLICY_HOLDER: policyHolderDataRoutes(prefix),
    ADDITIONAL_DATA: additionalDataRoutes(prefix),
    CONFIRM_DATA: confirmDataRoutes(prefix),
    RENEWAL_DATA: renewalRoutes(prefix),

    /** Modify Policy */
    ACTIONS: {
      route: `${prefix}/scelta-modifica`,
    },
    /** Modify Policy */
    RENEWAL: {
      route: `${prefix}/renewal`,
    },
    POLICY_SUMMARY: {
      route: `${prefix}/riepilogo`,
    },
    PROJECT_SUMMARY: {
      route: `${prefix}/riepilogo`,
    },
    /** Modify Policy */
    CANCEL: cancelRoutes(prefix),
    DISCONTINUE: discontinueRoutes(prefix),
  }) as const;

/**
 * Insert routes in the variable 'route' inside object.
 * Nested routes must be inside the object with the full route
 */
export const RoutePaths = {
  EMPTY: {
    route: '',
  },
  WILDCARD: {
    route: '**',
  },
  COMPONENTS_PREVIEW: {
    route: 'components-preview',
  },
  GENERIC_ERROR: {
    route: 'generic-error',
  },
  MAINTENANCE_PAGE: {
    route: 'manutenzione',
  },
  [Flow.NEW_QUOTATION]: projectRoutes('preventivo-casa'),
  [Flow.MODIFY_QUOTATION]: projectRoutes('modifica-preventivo-casa'),
  [Flow.MODIFY_POLICY]: projectRoutes('modifica-polizza-casa'),
  [Flow.MODIFY_RENEWAL]: projectRoutes('modifica-rinnovo-casa'),
} as const;
